// EditarProduto.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import {
  Box,
  Button,
  Stack,
  Flex,
  Text,
  Heading,
  Container,
  VStack,
  useColorModeValue,
  IconButton,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { useHistory, useParams } from 'react-router-dom';

import backend from '../../services/backend';
import { useAuth } from '../../hooks/auth';
import Page from '../../layout/IntegraAppPage';

import { SecaoInformacoesBasicas } from '../NovoProduto/components/SecaoInformacoesBasicas';
import { SecaoPrecoEstoque } from '../NovoProduto/components/SecaoPrecoEstoque';
import { SecaoAtributos } from '../NovoProduto/components/SecaoAtributos';
import type {
  AtributoProduto,
  AtributoCustomizado,
} from '../NovoProduto/components/SecaoAtributos';
import { SecaoUploadImagens } from '../NovoProduto/components/SecaoUploadImagens';
import ModalVisualizarImagens from '../NovoProduto/components/ModalVisualizarImagens';

type RouteParams = { id: string };

type SecaoCardProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
  bg: string;
  borderColor: string;
};

type ImagemEdicao = {
  id: string;
  url: string;
  arquivoOriginal?: File;
};

type ProdutoEdicaoApi = {
  id?: string | number;
  sku?: string;
  titulo_interno?: string;
  marca?: string;
  categoria_id?: string | number;
  codigo_barras?: string;
  ncm?: string;
  status?: string | number | boolean;
  descricao?: string;
  preco_custo?: string | number;
  estoque_atual?: string | number;
  peso?: string | number;
  altura?: string | number;
  largura?: string | number;
  profundidade?: string | number;
  imagens?: ImagemEdicao[];
  atributos_ml?: AtributoProduto[];
  atributos_customizados?: AtributoCustomizado[];
};

type InputLikeEvent = ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
>;

const SecaoCard = ({
  title,
  subtitle,
  children,
  bg,
  borderColor,
}: SecaoCardProps) => {
  const corTitulo = useColorModeValue('gray.800', 'whiteAlpha.900');
  const corSubtitulo = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      shadow="sm"
    >
      <VStack align="start" spacing={1} mb={6}>
        <Heading size="sm" fontWeight="bold" color={corTitulo}>
          {title}
        </Heading>

        {subtitle && (
          <Text fontSize="xs" color={corSubtitulo}>
            {subtitle}
          </Text>
        )}
      </VStack>

      {children}
    </Box>
  );
};

const EditarProduto: React.FC = () => {
  const { id } = useParams<RouteParams>();
  const history = useHistory();
  const toast = useToast();
  const { user } = useAuth();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const referenciaInputArquivo = useRef<HTMLInputElement>(null);

  const [carregando, setCarregando] = useState(false);
  const [indiceImagemModal, setIndiceImagemModal] = useState(0);

  const [atributos, setAtributos] = useState<AtributoProduto[]>([]);
  const [imagens, setImagens] = useState<ImagemEdicao[]>([]);
  const [atributosCustomizados, setAtributosCustomizados] = useState<
    AtributoCustomizado[]
  >([]);

  const [dadosFormulario, setDadosFormulario] = useState({
    codigoInterno: '',
    sku: '',
    titulo: '',
    marca: '',
    categoria: '',
    codigoBarras: '',
    ncm: '',
    status: 'Ativo',
    descricaoCurta: '',
    descricaoCompleta: '',
    preco: '0',
    precoPromocional: '',
    estoqueTotal: '0',
    estoqueReservado: '0',
    unidadeMedida: 'Unidade (UN)',
    peso: '',
    altura: '',
    largura: '',
    profundidade: '',
    permitirAutomatico: false,
  });

  // ===== CORES IGUAIS NOVO PRODUTO =====
  const cores = {
    fundoPagina: useColorModeValue('gray.50', 'gray.900'),
    fundoCard: useColorModeValue('white', 'gray.800'),
    corBorda: useColorModeValue('gray.200', 'gray.700'),
    fundoInput: useColorModeValue('white', 'gray.700'),
    textoLabel: useColorModeValue('gray.700', 'gray.300'),
    fundoSecaoIA: useColorModeValue('blue.50', 'gray.800'),
    corBordaSecaoIA: useColorModeValue('blue.200', 'gray.700'),
    corTextoIA: useColorModeValue('blue.700', 'blue.200'),
  };

  const corTextoPagina = useColorModeValue('gray.800', 'white');
  const corSubtitulo = useColorModeValue('gray.500', 'gray.400');

  // =====================================
  // BUSCAR PRODUTO
  // =====================================
  const carregarProduto = useCallback(async () => {
    try {
      setCarregando(true);

      const response = await backend.get(`/produtos/${id}`);
      const produto = response.data as ProdutoEdicaoApi;

      setDadosFormulario({
        codigoInterno: String(produto.id || ''),
        sku: produto.sku || '',
        titulo: produto.titulo_interno || '',
        marca: produto.marca || '',
        categoria: String(produto.categoria_id || ''),
        codigoBarras: produto.codigo_barras || '',
        ncm: produto.ncm || '',
        status: String(produto.status || 'Ativo'),
        descricaoCurta: produto.descricao || '',
        descricaoCompleta: produto.descricao || '',
        preco: String(produto.preco_custo || '0'),
        precoPromocional: '',
        estoqueTotal: String(produto.estoque_atual || '0'),
        estoqueReservado: '0',
        unidadeMedida: 'Unidade (UN)',
        peso: String(produto.peso || ''),
        altura: String(produto.altura || ''),
        largura: String(produto.largura || ''),
        profundidade: String(produto.profundidade || ''),
        permitirAutomatico: false,
      });

      setImagens(Array.isArray(produto.imagens) ? produto.imagens : []);

      const atributosSalvos = Array.isArray(produto.atributos_ml)
        ? produto.atributos_ml
        : [];

      setAtributos(
        atributosSalvos.map(attr => ({
          ...attr,
          id: attr.id || attr.atributo_externo_id,
          name: attr.name || attr.nome || attr.id || attr.atributo_externo_id,
          value: attr.value || attr.value_name || attr.valor_nome || '',
          value_name: attr.value_name || attr.valor_nome || attr.value || '',
          valor_nome: attr.valor_nome || attr.value_name || attr.value || '',
          value_id: attr.value_id || attr.valor_externo_id || null,
          valor_externo_id: attr.valor_externo_id || attr.value_id || null,
        })),
      );

      setAtributosCustomizados(
        Array.isArray(produto.atributos_customizados)
          ? produto.atributos_customizados
          : [],
      );
    } catch {
      toast({
        title: 'Erro ao carregar produto',
        status: 'error',
      });
    } finally {
      setCarregando(false);
    }
  }, [id, toast]);

  useEffect(() => {
    carregarProduto();
  }, [carregarProduto]);

  const lidarComMudancaInput = (e: InputLikeEvent) => {
    const { name, value } = e.target;

    setDadosFormulario(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const salvarEdicao = async () => {
    try {
      setCarregando(true);

      const formData = new FormData();

      Object.entries(dadosFormulario).forEach(([key, value]) => {
        formData.append(key, String(value));
      });

      formData.append('empresa_id', user?.empresa_id || '');
      formData.append('filial_id', user?.filial_id || '');

      imagens.forEach((img, index) => {
        if (img.arquivoOriginal) {
          formData.append(`imagens[${index}]`, img.arquivoOriginal);
        }
      });

      const atributosML = atributos
        .map(attr => ({
          id: attr.id,
          value_name: attr.value_name || attr.value || attr.valor_nome || '',
        }))
        .filter(attr => attr.id && attr.value_name);

      formData.append('atributos_ml', JSON.stringify(atributosML));

      await backend.put(`/produtos/${id}`, formData);

      toast({
        title: 'Produto atualizado com sucesso!',
        status: 'success',
      });

      history.push('/produtos/completo');
    } catch {
      toast({
        title: 'Erro ao salvar alterações',
        status: 'error',
      });
    } finally {
      setCarregando(false);
    }
  };

  return (
    <Page>
      <Box bg={cores.fundoPagina} minH="100vh" py={6} color={corTextoPagina}>
        <Container maxW="1600px">
          {/* TOPO */}
          <Flex align="center" mb={6} gap={4}>
            <IconButton
              aria-label="Voltar"
              icon={<FiArrowLeft />}
              variant="ghost"
              borderRadius="full"
              onClick={() => history.goBack()}
            />

            <VStack align="start" spacing={0}>
              <Heading size="md">Editar Produto</Heading>

              <Text fontSize="xs" color={corSubtitulo}>
                Atualize os dados do item abaixo
              </Text>
            </VStack>
          </Flex>

          <Stack spacing={6}>
            {/* INFO */}
            <SecaoCard
              title="Informações Básicas"
              bg={cores.fundoCard}
              borderColor={cores.corBorda}
            >
              <SecaoInformacoesBasicas
                dadosFormulario={dadosFormulario}
                lidarComMudancaInput={lidarComMudancaInput}
                carregando={carregando}
                cores={cores}
              />
            </SecaoCard>

            {/* ATRIBUTOS */}
            <SecaoAtributos
              atributos={atributos}
              setAtributos={setAtributos}
              atributosCustomizados={atributosCustomizados}
              setAtributosCustomizados={setAtributosCustomizados}
              adicionarAtributoCustomizado={() =>
                setAtributosCustomizados(prev => [
                  ...prev,
                  {
                    id: Date.now().toString(),
                    trait: '',
                    value: '',
                  },
                ])
              }
              cores={cores}
            />

            {/* PREÇO */}
            <SecaoCard
              title="Preços e Estoque"
              bg={cores.fundoCard}
              borderColor={cores.corBorda}
            >
              <SecaoPrecoEstoque
                dadosFormulario={dadosFormulario}
                lidarComMudancaInput={lidarComMudancaInput}
                cores={cores}
              />
            </SecaoCard>

            {/* IMAGENS */}
            <SecaoCard
              title={`Imagens (${imagens.length}/10)`}
              subtitle="Imagens do Anúncio"
              bg={cores.fundoCard}
              borderColor={cores.corBorda}
            >
              <input
                hidden
                ref={referenciaInputArquivo}
                type="file"
                accept="image/*"
                multiple
              />

              <SecaoUploadImagens
                imagens={imagens}
                abrirModalImagem={(idx: number) => {
                  setIndiceImagemModal(idx);
                  onOpen();
                }}
                removerImagem={(id: string) =>
                  setImagens(prev => prev.filter(img => img.id !== id))
                }
                carregarFotos={() => referenciaInputArquivo.current?.click()}
                carregando={carregando}
                cores={cores}
              />
            </SecaoCard>

            {/* BOTÕES */}
            <Flex justify="flex-end" gap={3} py={6} pb={12}>
              <Button
                variant="outline"
                size="sm"
                borderRadius="md"
                onClick={() => history.goBack()}
              >
                Cancelar
              </Button>

              <Button
                colorScheme="blue"
                size="sm"
                borderRadius="md"
                leftIcon={<FiSave />}
                isLoading={carregando}
                onClick={salvarEdicao}
              >
                Salvar Alterações
              </Button>
            </Flex>
          </Stack>

          <ModalVisualizarImagens
            isOpen={isOpen}
            onClose={onClose}
            imagens={imagens}
            indiceAtual={indiceImagemModal}
            setIndiceAtual={setIndiceImagemModal}
          />
        </Container>
      </Box>
    </Page>
  );
};

export default EditarProduto;
